import { useCallback } from 'react';
import { core_v1_Rule } from '@antimatterhq/asl-api-ts-v1';
import { Fieldset } from '@antimatterhq/ui/components/fieldset';
import Spacer from '@antimatterhq/ui/components/spacer';
import cronstrue from 'cronstrue';
import { Paragraph } from '@antimatterhq/ui/components/typography';

interface RuleMetadataReviewProps {
  rule: core_v1_Rule;
}

export function RuleMetadataReview({ rule }: RuleMetadataReviewProps) {
  const renderCron = useCallback((cron: string) => {
    try {
      return cronstrue.toString(cron);
    } catch {
      return `${cron} (invalid POSIX expression!)`;
    }
  }, []);

  return (
    <div>
      <div>
        <Fieldset.Field>
          <Fieldset.Label>
            Name {rule.metadata?.comment && ' & description:'}
          </Fieldset.Label>
          <Fieldset.Value>{rule.metadata?.name}</Fieldset.Value>
        </Fieldset.Field>
        <Fieldset.List>
          <Paragraph className="pr-8">{rule.metadata?.comment}</Paragraph>
        </Fieldset.List>
      </div>

      <Spacer size="sm" />

      <Fieldset.Field>
        <Fieldset.Label>Severity:</Fieldset.Label>
        <Fieldset.Value>{rule.spec.metadata?.severity}</Fieldset.Value>
      </Fieldset.Field>

      <Spacer size="sm" />

      <Fieldset.Field>
        <Fieldset.Label>Category:</Fieldset.Label>
        <Fieldset.Value>{rule.spec.metadata?.category}</Fieldset.Value>
      </Fieldset.Field>

      <Spacer size="sm" />

      <Fieldset.Field>
        <Fieldset.Label>Fidelity:</Fieldset.Label>
        <Fieldset.Value>{rule.spec.metadata?.fidelity}</Fieldset.Value>
      </Fieldset.Field>

      <Spacer size="sm" />

      <Fieldset.Field>
        <Fieldset.Label>Objective:</Fieldset.Label>
        <Fieldset.Value>
          {rule.spec.metadata?.objective || (
            <span className="italic">[none]</span>
          )}
        </Fieldset.Value>
      </Fieldset.Field>

      <Spacer size="lg" />

      <Fieldset.Field>
        <Fieldset.Label>Rule enabled:</Fieldset.Label>
        <Fieldset.Value>
          {rule.spec.schedule?.enabled ? 'Yes' : 'No'}
        </Fieldset.Value>
      </Fieldset.Field>

      <Spacer size="lg" />

      <Fieldset.Field>
        <Fieldset.Label>Processing schedule</Fieldset.Label>
      </Fieldset.Field>
      <Fieldset.List>
        {rule.spec?.schedule?.atLeastEvery && (
          <Fieldset.Field>
            <Fieldset.Label>At least every:</Fieldset.Label>
            <Fieldset.Value>{rule.spec?.schedule?.atLeastEvery}</Fieldset.Value>
          </Fieldset.Field>
        )}
        {rule.spec?.schedule?.continuous && (
          <Fieldset.Field>
            <Fieldset.Label>Continuous:</Fieldset.Label>
            <Fieldset.Value>Yes</Fieldset.Value>
          </Fieldset.Field>
        )}
        {rule.spec?.schedule?.exactly && (
          <Fieldset.Field>
            <Fieldset.Label>Exactly:</Fieldset.Label>
            <Fieldset.Value>
              {renderCron(rule.spec?.schedule?.exactly)}
            </Fieldset.Value>
          </Fieldset.Field>
        )}
        {rule.spec?.schedule?.computeGroup && (
          <Fieldset.Field>
            <Fieldset.Label>Compute resource:</Fieldset.Label>
            <Fieldset.Value>{rule.spec?.schedule?.computeGroup}</Fieldset.Value>
          </Fieldset.Field>
        )}
      </Fieldset.List>

      <Spacer size="lg" />

      <Fieldset.Field>
        <Fieldset.Label>Annotations</Fieldset.Label>
      </Fieldset.Field>
      <Fieldset.List>
        {rule.metadata?.annotations ? (
          Object.entries(rule.metadata.annotations).map(([key, value]) => (
            <Fieldset.Field key={key}>
              <Fieldset.Label>{key}:</Fieldset.Label>
              <Fieldset.Value>{value}</Fieldset.Value>
            </Fieldset.Field>
          ))
        ) : (
          <Fieldset.Value>
            <span className="italic">[None]</span>
          </Fieldset.Value>
        )}
      </Fieldset.List>
    </div>
  );
}
